/**
 * React Query Client Configuration
 * Centralized configuration for TanStack Query
 */

import { QueryClient, DefaultOptions } from '@tanstack/react-query'
import { captureException } from '@/lib/errors'

/**
 * Default query options
 */
const queryConfig: DefaultOptions = {
  queries: {
    // Stale time: Data is considered fresh for 30 seconds
    staleTime: 30 * 1000,

    // Cache time: Unused data stays in cache for 5 minutes
    gcTime: 5 * 60 * 1000,

    // Retry configuration
    retry: (failureCount, error: any) => {
      // Don't retry on 4xx errors (client errors)
      if (error?.statusCode >= 400 && error?.statusCode < 500) {
        return false
      }
      // Retry up to 3 times for 5xx errors
      return failureCount < 3
    },

    // Retry delay with exponential backoff
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

    // Refetch on window focus for critical data
    refetchOnWindowFocus: true,

    // Refetch on reconnect
    refetchOnReconnect: true,

    // Don't refetch on mount if data is fresh
    refetchOnMount: false,

    // Network mode: online or always
    networkMode: 'online',
  },

  mutations: {
    // Retry mutations once
    retry: 1,

    // Network mode
    networkMode: 'online',

    // Global error handler for mutations
    onError: (error) => {
      // Log to Sentry
      captureException(error, {
        context: 'mutation',
        type: 'react-query',
      })
    },
  },
}

/**
 * Create a new QueryClient instance
 */
export function createQueryClient() {
  return new QueryClient({
    defaultOptions: queryConfig,
  })
}

/**
 * Singleton QueryClient instance
 * Used for server-side rendering and client-side
 */
let browserQueryClient: QueryClient | undefined = undefined

export function getQueryClient() {
  if (typeof window === 'undefined') {
    // Server: always create a new client
    return createQueryClient()
  } else {
    // Browser: create client once
    if (!browserQueryClient) {
      browserQueryClient = createQueryClient()
    }
    return browserQueryClient
  }
}
